<template>
  <div class="flex items-start" :class="props.containerClass" @click.stop="toggle">
    <div class="flex-shrink-0 mt-0.5">
      <button type="button" :class="[
          'relative inline-flex items-center justify-center w-5 h-5 rounded border transition-colors duration-200 ease-in-out focus:outline-none',
          checkboxColorClasses,
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        ]" :disabled="disabled" :aria-checked="modelValue">
        <span v-if="indeterminate" class="material-symbols-outlined text-white !text-[14px] absolute">
          remove
        </span>
        <span v-else-if="modelValue" class="material-symbols-outlined text-white !text-[14px] absolute">
          check
        </span>
        <span class="invisible">
          check
        </span>
      </button>
    </div>

    <div v-if="label || description" class="ml-2">
      <label v-if="label" :class="[labelClass, additionalLabelClass, disabled ? 'opacity-60' : '']" class="text-sm cursor-pointer"> 
        {{ label }}<span v-if="required" class="text-primary">*</span>
      </label>
      <p v-if="description" :class="[descriptionClass, additionalDescriptionClass]" class="text-xs mt-1">
        {{ description }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
// Removed UUID dependency to improve performance
// Removed unnecessary component imports for better performance

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  indeterminate: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  information: {
    type: String,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'accent', 'header', 'indigo', 'neutral'].includes(value),
  },
  containerClass: {
    type: String,
    default: '',
  },
  labelClass: {
    type: String,
    default: 'font-semibold text-secondary',
  },
  additionalLabelClass: {
    type: String,
    default: '',
  },
  descriptionClass: {
    type: String,
    default: 'text-gray-600',
  },
  additionalDescriptionClass: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'change'])

// Using a more efficient ID generation approach
const inputId = computed(() => `checkbox-${props.label?.replace(/\s+/g, '-').toLowerCase() || 'cb'}`)

// Pre-defined color classes to avoid recalculation
const colorVariations = {
  primary: {
    active: 'bg-primary border-primary',
    inactive: 'bg-white border-gray-300',
  },
  secondary: {
    active: 'bg-secondary border-secondary',
    inactive: 'bg-white border-gray-300',
  },
  accent: {
    active: 'bg-accent border-accent',
    inactive: 'bg-white border-gray-300',
  },
  header: {
    active: 'bg-header border-header',
    inactive: 'bg-white border-gray-300',
  },
  indigo: {
    active: 'bg-indigo border-indigo',
    inactive: 'bg-white border-gray-300',
  },
  neutral: {
    active: 'bg-neutral border-neutral',
    inactive: 'bg-white border-gray-300',
  },
}

// Simplified color class calculation
const checkboxColorClasses = computed(() => {
  if (props.indeterminate) {
    return 'bg-primary/70 border-primary'
  }
  const colorVariation = colorVariations[props.color] || colorVariations.primary
  return props.modelValue ? colorVariation.active : colorVariation.inactive
})

const toggle = () => {
  if (!props.disabled) {
    emit('update:modelValue', !props.modelValue)
    emit('change', !props.modelValue)
  }
}
</script>
